import React, { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import BlockIcon from "@mui/icons-material/Block";
import EditIcon from "@mui/icons-material/Edit";
import LoginIcon from "@mui/icons-material/Login";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ShieldIcon from "@mui/icons-material/Shield";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EventNoteIcon from "@mui/icons-material/EventNote";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GroupsIcon from "@mui/icons-material/Groups";

import { formatUserType, safeDateTime } from "../../../utils";
import IdentitySection from "./IdentitySection";
import ChangeLogs from "./ChangeLogs";
import RefreshSessions from "./RefreshSession";
import GenericDialog from "../../../library/Dialog";
import OrganizationDetails from "./Organization";

export function safeDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

function maskId(value, keep = 6) {
  const s = String(value || "");
  if (!s) return "—";
  if (s.length <= keep * 2) return s;
  return `${s.slice(0, keep)}…${s.slice(-keep)}`;
}

function CopyIconButton({ value, label = "Copy" }) {
  return (
    <Tooltip title={label}>
      <span>
        <IconButton
          size='small'
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard?.writeText(String(value || ""));
          }}
          disabled={!value}
        >
          <ContentCopyIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </span>
    </Tooltip>
  );
}

export function MaskedId({ label, value }) {
  const [reveal, setReveal] = useState(false);
  const shown = reveal ? String(value || "") : maskId(value);
  return (
    <Stack spacing={0.5}>
      <Typography variant='caption' color='text.secondary' fontWeight={700}>
        {label}
      </Typography>
      <Stack direction='row' spacing={0.5} alignItems='center'>
        <Typography
          variant='body2'
          sx={{ fontFamily: "monospace" }}
          color='text.secondary'
          noWrap
        >
          {shown || "—"}
        </Typography>
        <CopyIconButton value={value} label={`Copy ${label}`} />
        <Tooltip title={reveal ? "Hide" : "Reveal"}>
          <IconButton
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              setReveal((v) => !v);
            }}
            disabled={!value}
          >
            {reveal ? (
              <VisibilityOffIcon sx={{ fontSize: 18 }} />
            ) : (
              <VisibilityIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

function Card({ title, icon, children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: (t) => alpha(t.palette.divider, 0.14),
        bgcolor: "background.paper",
        ...sx,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          borderBottom: "1px solid",
          borderColor: (t) => alpha(t.palette.divider, 0.12),
          bgcolor: (t) => alpha(t.palette.primary.main, 0.02),
        }}
      >
        <Stack direction='row' spacing={1} alignItems='center'>
          <Box sx={{ color: "text.secondary", display: "flex" }}>{icon}</Box>
          <Typography fontWeight={800} color='text.secondary'>
            {title}
          </Typography>
        </Stack>
      </Box>
      <Box sx={{ p: 2, overflow: "auto" }}>{children}</Box>
    </Paper>
  );
}

function EmptyState({ title, subtitle }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px dashed",
        borderColor: (t) => alpha(t.palette.divider, 0.25),
        bgcolor: (t) => alpha(t.palette.primary.main, 0.02),
      }}
    >
      <Typography fontWeight={800} color='text.secondary'>
        {title}
      </Typography>
      <Typography variant='body2' color='text.secondary'>
        {subtitle}
      </Typography>
    </Box>
  );
}

const AccountDetails = ({ loading, data, u_id }) => {
  const theme = useTheme();

  const profile = data?.profile || null;
  const userAuth = profile?.user_auth || null;
  const sessionInfo = data?.session_info || null;
  const companies = Array.isArray(data?.companies) ? data.companies : [];

  const fullName = profile?.name || "—";
  const email = profile?.email || "—";
  const phone = [profile?.phone_code, profile?.phone_number]
    .filter(Boolean)
    .join(" ");
  const address = profile?.address || "—";

  const isRoot = userAuth?.is_root === true;
  const enabled = userAuth?.enabled !== false;
  const status = userAuth?.status || "—";
  const healthLabel = !enabled
    ? "Disabled"
    : status === "active"
      ? "Active"
      : status;
  const healthColor = !enabled
    ? "default"
    : status === "active"
      ? "success"
      : status === "banned"
        ? "error"
        : status === "pending"
          ? "warning"
          : "default";

  const typeLabel = formatUserType(userAuth?.user_type);
  const roleLabel = userAuth?.user_role || "—";

  const authProvider = userAuth?.auth_provider || "—";
  const lastVerifiedAt = safeDateTime(userAuth?.last_verified_at);
  const lastLoginAt = safeDateTime(userAuth?.last_login_at);

  const isLogin = userAuth?.is_login === true;
  const isBlocked = userAuth?.is_login_blocked === true;

  const currentLoginAt = safeDateTime(sessionInfo?.current_login_at);
  const hasActiveSession = sessionInfo?.has_active_session === true;
  const refreshSessions = Array.isArray(sessionInfo?.refresh_sessions)
    ? sessionInfo.refresh_sessions
    : [];

  const subscription = profile?.subscription || null;
  const planName = subscription?.plan?.name || "—";
  const subStatus = subscription?.status || "—";
  const nextBilling = safeDate(subscription?.next_billing_date);
  const trialEnd =
    subStatus === "trialing" ? safeDate(subscription?.trial_end) : "NA";
  const verified = subscription?.verified === true;
  const subscriptionLogs = Array.isArray(subscription?.change_logs)
    ? subscription.change_logs
    : [];

  const organization = subscription?.organization || null;
  const members = Array.isArray(subscription?.users) ? subscription.users : [];
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);

  const customerId = profile?.customer_id || "—";
  const subscriptionId = profile?.subscription_id || "—";

  const createdTime = safeDateTime(profile?.created_time);
  const createdBy = profile?.created_by || "—";
  const expireDate = profile?.user_auth?.expire_date
    ? safeDateTime(profile.user_auth.expire_date)
    : "N/A";

  const heroInitial = (fullName?.[0] || email?.[0] || "?").toUpperCase();

  const heroBg = useMemo(
    () => ({
      background:
        theme.palette.mode === "dark"
          ? `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.18,
            )} 0%, ${alpha(theme.palette.secondary.main, 0.12)} 100%)`
          : `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.12,
            )} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
    }),
    [theme],
  );

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: (t) => alpha(t.palette.divider, 0.14),
          height: "100%", // 确保骨架屏撑满卡片高度
        }}
      >
        <Stack spacing={2}>
          {/* 模拟标题或图标区域 */}
          <Stack direction='row' alignItems='center' spacing={1}>
            <Skeleton variant='circular' width={24} height={24} />
            <Skeleton variant='text' width={120} height={28} />
          </Stack>

          {/* 模拟几行文本内容 */}
          <Box sx={{ pt: 1 }}>
            <Skeleton variant='text' width='80%' />
            <Skeleton variant='text' width='65%' />
            <Skeleton variant='text' width='90%' />
          </Box>

          {/* 模拟底部的按钮或标签区域 */}
          <Stack direction='row' spacing={1} sx={{ mt: 2 }}>
            <Skeleton variant='rounded' width={80} height={32} />
            <Skeleton variant='rounded' width={60} height={32} />
          </Stack>
        </Stack>
      </Paper>
    );
  }

  if (!data) {
    return <Alert severity='info'>No data.</Alert>;
  }

  return (
    <Stack spacing={2.5}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          border: "1px solid",
          borderColor: (t) => alpha(t.palette.divider, 0.14),
          ...heroBg,
        }}
      >
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent='space-between'
          >
            <Stack direction='row' spacing={2} alignItems='center'>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: alpha(theme.palette.primary.main, 0.18),
                  color: "primary.main",
                  fontWeight: 900,
                }}
              >
                {heroInitial}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Typography variant='h5' fontWeight={700} noWrap>
                    {fullName}
                  </Typography>
                  {isLogin ? (
                    <Tooltip title='Online'>
                      <FiberManualRecordIcon
                        sx={{ color: "success.main", fontSize: 18 }}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title='Offline'>
                      <FiberManualRecordIcon
                        sx={{ color: "grey.500", fontSize: 18 }}
                      />
                    </Tooltip>
                  )}
                </Stack>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  sx={{ mt: 0.5 }}
                >
                  <MaskedId label='User Id' value={profile?.u_id || u_id} />
                  <Divider
                    flexItem
                    orientation='vertical'
                    sx={{
                      display: { xs: "none", sm: "block" },
                      mx: 1,
                      borderColor: alpha(theme.palette.divider, 0.25),
                    }}
                  />
                  <Stack direction='column'>
                    <Typography variant='body2' color='text.secondary' noWrap>
                      <EmailIcon
                        sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }}
                      />
                      {email}
                    </Typography>
                    <Typography variant='body2' color='text.secondary' noWrap>
                      <PhoneIcon
                        sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }}
                      />
                      {phone || "—"}
                    </Typography>
                    <Typography variant='body2' color='text.secondary' noWrap>
                      <BusinessIcon
                        sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }}
                      />
                      {address || "—"}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent='flex-end'
            >
              <Stack direction='row' spacing={1} justifyContent='flex-end'>
                <Tooltip title='View organization & members'>
                  <span>
                    <IconButton
                      onClick={() => setOrgDialogOpen(true)}
                      disabled={!organization && members.length === 0}
                      sx={{
                        border: "1px solid",
                        borderColor: theme.palette.primary.main,
                        borderRadius: 2,
                        color: theme.palette.primary.main,
                        background: alpha(theme.palette.primary.main, 0.1),
                        transition: "background 0.18s, border-color 0.18s",
                        "&:hover": {
                          background: alpha(theme.palette.primary.main, 0.18),
                          borderColor: theme.palette.primary.dark,
                          color: theme.palette.primary.dark,
                        },
                        "&.Mui-disabled": {
                          borderColor: theme.palette.action.disabled,
                          color: theme.palette.action.disabled,
                          background: "transparent",
                        },
                      }}
                    >
                      <GroupsIcon fontSize='small' />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Paper>

      <GenericDialog
        open={orgDialogOpen}
        onClose={() => setOrgDialogOpen(false)}
        title='Organization'
        maxWidth='md'
        cancelText='Close'
        confirmText='Done'
        onConfirm={() => setOrgDialogOpen(false)}
        content={
          <OrganizationDetails organization={organization} members={members} />
        }
      />

      <Grid container spacing={2}>
        <Grid container spacing={2}>
          {/* 左侧：Security & Auth Center + Subscription & Billing */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: { xs: "auto", md: "100%" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <Card title='Security & Auth Center' icon={<ShieldIcon />}>
                <Stack spacing={1.5}>
                  {isBlocked ? (
                    <Alert severity='error' icon={<WarningAmberIcon />}>
                      Risk control: this account is blocked
                      (is_login_blocked=true)
                    </Alert>
                  ) : null}

                  <IdentitySection
                    authProvider={authProvider}
                    lastVerifiedAt={lastVerifiedAt}
                    lastLoginAt={lastLoginAt}
                    currentLoginAt={currentLoginAt}
                    healthLabel={healthLabel}
                    healthColor={healthColor}
                    typeLabel={typeLabel}
                    roleLabel={roleLabel}
                    isRoot={isRoot}
                  />
                </Stack>
              </Card>

              <Card
                title='Subscription & Billing'
                icon={<BusinessIcon />}
                sx={{ mt: 2 }}
              >
                <Stack spacing={2}>
                  <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                  >
                    <Box>
                      <Typography
                        variant='h6'
                        fontWeight={900}
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <BusinessIcon fontSize='small' color='action' />
                        {planName}
                      </Typography>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        mt={0.5}
                      >
                        Current Subscription Plan{" "}
                        {verified ? (
                          <CheckCircleIcon
                            color='success'
                            sx={{ ml: 1, fontSize: 20 }}
                          />
                        ) : (
                          <Chip
                            label='Unverified'
                            size='small'
                            color='warning'
                            sx={{ ml: 1, fontWeight: 500 }}
                          />
                        )}
                      </Typography>
                    </Box>
                    <Chip
                      label={
                        subStatus.charAt(0).toUpperCase() + subStatus.slice(1)
                      }
                      size='small'
                      color={subStatus === "active" ? "success" : "default"}
                      sx={{ fontWeight: 700 }}
                    />
                  </Stack>

                  <Divider />

                  <Stack spacing={1.5}>
                    <Stack direction='row' alignItems='center' spacing={1}>
                      <CalendarTodayIcon
                        fontSize='small'
                        color='action'
                        sx={{ fontSize: 18 }}
                      />
                      <Typography variant='body2'>
                        Next billing:{" "}
                        <Typography component='span' fontWeight={600}>
                          {nextBilling}
                        </Typography>
                      </Typography>
                    </Stack>
                    {trialEnd && (
                      <Stack direction='row' alignItems='center' spacing={1}>
                        <CalendarTodayIcon
                          fontSize='small'
                          color='disabled'
                          sx={{ fontSize: 18 }}
                        />
                        <Typography variant='body2' color='text.secondary'>
                          Trial end:{" "}
                          <Typography component='span'>{trialEnd}</Typography>
                        </Typography>
                      </Stack>
                    )}
                  </Stack>

                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "background.default",
                      borderRadius: 1,
                      border: "1px dashed",
                      borderColor: "divider",
                    }}
                  >
                    <Stack spacing={1}>
                      <MaskedId
                        label='Subscription ID'
                        value={subscriptionId}
                      />
                      <MaskedId label='Hitpay ID' value={customerId} />
                    </Stack>
                  </Box>
                </Stack>
              </Card>
            </Box>
          </Grid>

          {/* 右侧：Subscription Activity，确保高度与左侧一致 */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <Card
                title='Subscription Activity'
                icon={<EventNoteIcon />}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  height: "100%",
                }}
                contentSx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                  {subscriptionLogs.length === 0 ? (
                    <EmptyState title='No activity' />
                  ) : (
                    <ChangeLogs subscriptionLogs={subscriptionLogs} />
                  )}
                </Box>
              </Card>
            </Box>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={2}
          sx={{
            mt: 0,
            height: { xs: "auto", md: 600 }, // Adjust the fixed height as needed
            alignItems: "stretch",
          }}
        >
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: { xs: "auto", md: "100%" },
            }}
          >
            <Card
              title={`Companies (${companies.length})`}
              icon={<BusinessIcon />}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
              contentSx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                {companies.length === 0 ? (
                  <Typography variant='body2' color='text.secondary'>
                    None
                  </Typography>
                ) : (
                  companies.map((c) => (
                    <Stack
                      key={c.id}
                      direction='row'
                      justifyContent='space-between'
                      sx={{ py: 0.5 }}
                    >
                      <Typography variant='body2'>{c.name}</Typography>
                      <Typography variant='caption' color='text.secondary'>
                        <MaskedId value={c?.c_id || "-"} />
                      </Typography>
                    </Stack>
                  ))
                )}
              </Box>
            </Card>
          </Grid>

          {/* Sessions & Lifecycle */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: { xs: "auto", md: "100%" },
            }}
          >
            <Card
              title='Sessions & Lifecycle'
              icon={<EventNoteIcon />}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
              contentSx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <RefreshSessions
                refreshSessions={refreshSessions}
                createdTime={createdTime}
                expireDate={expireDate}
              />
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default AccountDetails;
